import React, { useState, useEffect } from 'react';
import { updateUserProfile } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

const EditProfileModal = ({ isOpen, onClose, profileData, onProfileUpdate, kycStatus }) => {
    const { showSuccess, showError } = useToast();
    
    // Helper to clean up name: remove literal null/undefined tokens, collapse spaces, and trim
    const sanitizeName = (value) => {
        const str = (value ?? '').toString();
        return str
            .replace(/\b(null|undefined)\b/gi, ' ') // remove literal words
            .replace(/\s+/g, ' ')                    // collapse multiple spaces
            .trim();                                  // trim edges
    };

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        pincode: '',
        expectedSalary: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isNameReadOnly, setIsNameReadOnly] = useState(false);

    // Debug function to test API call manually
    window.debugProfileUpdate = async () => {
        console.log('🔍 DEBUG: Testing profile update...');
        const candidateId = localStorage.getItem('candidateId');
        const userMobileNumber = localStorage.getItem('userMobileNumber');
        const user = localStorage.getItem('user');
        
        console.log('🔍 DEBUG: candidateId:', candidateId);
        console.log('🔍 DEBUG: userMobileNumber:', userMobileNumber);
        console.log('🔍 DEBUG: user object:', user ? JSON.parse(user) : null);
        
        const testData = {
            fullName: 'Test Name',
            email: 'test@example.com',
            pincode: '123456',
            expectedSalary: '50000'
        };
        
        try {
            const result = await updateUserProfile(testData);
            console.log('🔍 DEBUG: API result:', result);
        } catch (error) {
            console.error('🔍 DEBUG: API error:', error);
        }
    };

    // Load profile data from props when modal opens - only once per modal open
    useEffect(() => {
        if (isOpen && loading) {
            // Only set form data when modal first opens (loading is true)
            if (profileData) {
                const salaryValue = profileData.expectedSalary || profileData.salaryMax || '';
                
                setFormData({
                    fullName: sanitizeName(profileData.fullName || ''),
                    email: profileData.email || '',
                    pincode: profileData.pincode || '',
                    expectedSalary: salaryValue
                });

                // Check if candidate name matches KYC Aadhaar name
                const candidateName = sanitizeName(profileData.fullName || '').toLowerCase();
                const aadhaarName = kycStatus?.nameOnAadhaar ? sanitizeName(kycStatus.nameOnAadhaar).toLowerCase() : null;
                
                // Set read-only if both names are the same
                const namesMatch = candidateName === aadhaarName && aadhaarName !== null;
                setIsNameReadOnly(namesMatch);
            } else {
                setFormData({
                    fullName: '',
                    email: '',
                    pincode: '',
                    expectedSalary: ''
                });
                setIsNameReadOnly(false);
            }
            setLoading(false);
        } else if (!isOpen) {
            // Reset loading state when modal is closed
            setLoading(true);
            setIsNameReadOnly(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, profileData, kycStatus]);



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    const handleSubmit = async (e) => {
        console.log('[EditProfileModal] 🔥 handleSubmit called!');
        e.preventDefault();
        console.log('[EditProfileModal] 🔥 preventDefault called, form submission prevented');
        
        try {
            setSaving(true);
            console.log('[EditProfileModal] 🔥 setSaving(true) called');
            const payload = {
                ...formData,
                fullName: sanitizeName(formData.fullName),
            };
            console.log('[EditProfileModal] Updating profile with data:', payload);
            // Validate full name: letters and spaces only, 2-50 chars (only if not read-only)
            if (!isNameReadOnly) {
                const nameRegex = /^[A-Za-z ]{2,50}$/;
                if (!nameRegex.test(payload.fullName)) {
                    console.log('[EditProfileModal] Validation failed: Invalid full name');
                    showError('Full Name should contain letters and spaces only (2-50 characters).');
                    setSaving(false);
                    return;
                }
            }

            // Validate email format if provided (email is optional)
            if (payload.email && payload.email.trim() !== '') {
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegex.test(payload.email.trim())) {
                    console.log('[EditProfileModal] Validation failed: Invalid email format');
                    showError('Please enter a valid email address (e.g., user@example.com).');
                    setSaving(false);
                    return;
                }
            }

            // Validate pincode: required and must be exactly 6 digits
            if (!payload.pincode || String(payload.pincode).trim() === '') {
                console.log('[EditProfileModal] Validation failed: PIN Code is required');
                showError('PIN Code is required. Please enter a 6-digit PIN code.');
                setSaving(false);
                return;
            }
            const pincodeRegex = /^[0-9]{6}$/;
            if (!pincodeRegex.test(String(payload.pincode))) {
                console.log('[EditProfileModal] Validation failed: Invalid PIN Code format');
                showError('PIN Code must be exactly 6 digits.');
                setSaving(false);
                return;
            }
            
            // Check if candidate ID is available
            const candidateId = localStorage.getItem('candidateId');
            console.log('[EditProfileModal] 🔥 Candidate ID from localStorage:', candidateId);
            
            console.log('[EditProfileModal] 🔥 About to call updateUserProfile API...');
            console.log('[EditProfileModal] 🔥 Payload being sent:', payload);
            const result = await updateUserProfile(payload);
            console.log('[EditProfileModal] 🔥 updateUserProfile API returned:', result);
            console.log('[EditProfileModal] 🔥 API result success:', result?.success);
            console.log('[EditProfileModal] 🔥 API result error:', result?.error);
            
            if (result.success) {
                console.log('[EditProfileModal] ✅ Profile updated successfully');
                
                // Refresh profile data in parent component
                console.log('[EditProfileModal] Refreshing profile data in parent component...');
                if (onProfileUpdate) {
                    await onProfileUpdate();
                    console.log('[EditProfileModal] Profile data refreshed in parent component');
                }
                
                // Persist updated name to localStorage user object and notify app
                try {
                    const userStr = localStorage.getItem('user');
                    const userObj = userStr ? JSON.parse(userStr) : {};
                    const updatedName = payload.fullName || userObj.name || '';
                    const updatedUser = {
                        ...userObj,
                        name: updatedName,
                        fullName: updatedName,
                        initials: updatedName ? updatedName.split(' ').map(n => n[0]).join('').toUpperCase() : (userObj.initials || 'U')
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    // Dispatch a custom event so AuthContext/Header can react immediately
                    window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: { fullName: updatedName } }));
                    console.log('[EditProfileModal] 🔔 Dispatched userProfileUpdated event with name:', updatedName);
                } catch (e) {
                    console.warn('[EditProfileModal] Could not persist updated user to storage:', e);
                }
                
                // Show success message to user
                showSuccess('Profile updated successfully!');
                
                // Close modal only after successful update and refresh
                onClose();
            } else {
                console.log('[EditProfileModal] ❌ Failed to update profile:', result.error);
                const errorMessage = result.error || 'Unknown error occurred';
                showError(`Failed to update profile: ${errorMessage}. Please try again.`);
            }
        } catch (error) {
            console.error('[EditProfileModal] Error updating profile:', error);
            console.error('[EditProfileModal] Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            showError(`An error occurred while updating profile: ${error.message}. Please try again.`);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            display: 'flex',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
                overflow: 'hidden'
            }}>
                {/* Fixed Header */}
                <div style={{
                    position: 'sticky',
                    top: 0,
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    color: 'white',
                    padding: '24px',
                    borderBottom: '1px solid #e0e7ff',
                    zIndex: 10
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h3 style={{
                            margin: 0,
                            color: 'white',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}>
                            <i className="fas fa-edit" style={{ color: 'rgba(255,255,255,0.9)' }}></i>
                            Edit Profile
                        </h3>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '22px',
                                cursor: 'pointer',
                                color: 'white',
                                padding: 0,
                                borderRadius: '50%',
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.2s, box-shadow 0.2s'
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                            onFocus={(e) => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.5), 0 0 0 6px rgba(37, 99, 235, 0.5)';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div style={{
                    padding: '2rem',
                    overflowY: 'auto',
                    flex: 1
                }}>
                    <form onSubmit={handleSubmit} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.5rem'
                    }}>                        
                        {/* Loading Indicator */}
                        {loading && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem',
                                background: '#f8fafc',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem', color: '#3b82f6' }}></i>
                                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading profile data...</span>
                            </div>
                        )}
                        {/* Full Name */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '0.875rem'
                            }}>
                                <i className="fas fa-user" style={{ marginRight: '0.5rem', color: '#6b7280' }}></i>
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={isNameReadOnly ? undefined : handleInputChange}
                                required
                                disabled={loading}
                                readOnly={isNameReadOnly}
                                style={{
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    transition: 'border-color 0.2s',
                                    opacity: loading ? 0.6 : 1
                                }}
                                placeholder="Enter your full name"
                                maxLength="50"
                                pattern="[A-Za-z ]{2,50}"
                                title="Use letters and spaces only (2-50 characters)"
                                autoComplete="off"
                                onInput={isNameReadOnly ? undefined : (e) => {
                                    const cleaned = e.target.value
                                        .replace(/[^A-Za-z ]/g, '')
                                        .replace(/\s{2,}/g, ' ');
                                    if (e.target.value !== cleaned) {
                                        e.target.value = cleaned;
                                    }
                                }}
                            />
                        </div>

                        {/* Email Address */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '0.875rem'
                            }}>
                                <i className="fas fa-envelope" style={{ marginRight: '0.5rem', color: '#6b7280' }}></i>
                                Email Address
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                disabled={loading}
                                pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                                title="Please enter a valid email address (e.g., user@example.com)"
                                style={{
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '12px',
                                    fontSize: '0.875rem',
                                    transition: 'border-color 0.2s',
                                    opacity: loading ? 0.6 : 1
                                }}
                                placeholder="Enter your email address (e.g., user@example.com)"
                            />
                        </div>

                        {/* PIN Code */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '0.875rem'
                            }}>
                                <i className="fas fa-map-pin" style={{ marginRight: '0.5rem', color: '#6b7280' }}></i>
                                PIN Code *
                            </label>
                            <input
                                type="text"
                                name="pincode"
                                value={formData.pincode}
                                onChange={(e) => {
                                    // Only allow numeric input
                                    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
                                    setFormData(prev => ({
                                        ...prev,
                                        pincode: value
                                    }));
                                }}
                                required
                                disabled={loading}
                                pattern="[0-9]{6}"
                                maxLength="6"
                                style={{
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    transition: 'border-color 0.2s',
                                    opacity: loading ? 0.6 : 1
                                }}
                                placeholder="Enter 6-digit PIN code"
                                title="PIN Code is required (6 digits)"
                            />
                        </div>

                        {/* Expected Salary */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{
                                fontWeight: '600',
                                color: '#374151',
                                fontSize: '0.875rem'
                            }}>
                                <span style={{ marginRight: '0.5rem', color: '#6b7280', fontSize: '1rem' }}>₹</span>
                                Expected Salary (per month)
                            </label>
                            <input
                                type="text"
                                name="expectedSalary"
                                value={formData.expectedSalary}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
                                    setFormData(prev => ({
                                        ...prev,
                                        expectedSalary: value
                                    }));
                                }}
                                placeholder="Enter expected salary (e.g., 50000)"
                                disabled={loading}
                                maxLength="6"
                                style={{
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    transition: 'border-color 0.2s',
                                    opacity: loading ? 0.6 : 1
                                }}
                            />
                        </div>
                    </form>
                </div>

                {/* Fixed Action Buttons */}
                <div style={{
                    position: 'sticky',
                    bottom: 0,
                    background: 'white',
                    borderTop: '1px solid #e5e7eb',
                    padding: '1.5rem 2rem',
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                            minWidth: '120px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="editProfileForm"
                        disabled={saving || loading}
                        style={{
                            background: saving ? '#94a3b8' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                            color: 'white',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            cursor: saving || loading ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                            opacity: saving || loading ? 0.7 : 1,
                            minWidth: '140px'
                        }}
                        onClick={handleSubmit}
                    >
                        {saving ? (
                            <>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save" style={{ marginRight: '0.5rem' }}></i>
                                Save Changes
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditProfileModal;
